import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack } from "@mui/material";
import {
  Form,
  Formik,
  FormikErrors,
  FormikTouched,
  FormikValues,
  setIn
} from "formik";
import * as Yup from "yup";
import { ValidationError } from "yup";

export type ModulePlanFormBaseProps<T> = {
  initialValues: T;
  validationSchema: Yup.ObjectSchema<any>;
  isLoading: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  page?: number;
  totalPages?: number;
  handleNext?: () => void;
  getStepValidationSchema?: (
    currentStep: number
  ) => Promise<Yup.ObjectSchema<any>>;
  children: React.ReactNode;
};

export const ModulePlanFormBase = <T extends FormikValues>({
  initialValues,
  validationSchema,
  isLoading,
  onClose,
  onSave,
  children,
  page = 0,
  totalPages = 1,
  handleNext,
  getStepValidationSchema
}: ModulePlanFormBaseProps<T>) => {
  const handleNextStep = async (
    values: T,
    setErrors: (errors: FormikErrors<T>) => void,
    setTouched: (touched: FormikTouched<T>) => void
  ) => {
    const schema = getStepValidationSchema
      ? await getStepValidationSchema(page)
      : validationSchema;

    try {
      schema.validateSync(values, { abortEarly: false });
      if (page === totalPages - 1) {
        onSave(values);
      } else {
        handleNext?.();
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        const stepErrors: FormikErrors<any> = {};

        if (err.inner && err.inner.length > 0) {
          err.inner.forEach((validationError) => {
            if (validationError.path) {
              stepErrors[validationError.path] = validationError.message;
            }
          });
        } else if (err.path) {
          stepErrors[err.path] = err.message;
        }

        let touchedFields = {};
        Object.keys(stepErrors).forEach((key) => {
          touchedFields = setIn(touchedFields, key, true);
        });

        setErrors(stepErrors);
        setTouched(touchedFields);
      }
    }
  };

  return (
    <Box sx={{ width: "100%" }} padding={2}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {}}
        enableReinitialize
      >
        {({ values, isSubmitting, setErrors, setTouched }) => (
          <Form>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {children}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {page === totalPages - 1 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <LoadingButton
                  type="button"
                  onClick={() => handleNextStep(values, setErrors, setTouched)}
                  variant="contained"
                  color="primary"
                  loading={isLoading || isSubmitting}
                  sx={{ flex: 1 }}
                >
                  {page === totalPages - 1 ? "Save" : "Next"}
                </LoadingButton>
              </Stack>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
